import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Mail, MailCheck, MailX, MailWarning, Loader2,
  Copy, ExternalLink, ClipboardCheck, Inbox,
} from "lucide-react";

import { API } from "@/lib/api";
import { readToken } from "@/lib/tokenStore";

function authHeaders() {
  const t = readToken();
  return t ? { Authorization: `Bearer ${t}` } : {};
}

function formatDate(iso) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleString("de-DE", {
      day: "2-digit", month: "2-digit", year: "2-digit",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

const STATUS_LABEL = {
  pending: "Ausstehend",
  sent: "Gesendet",
  failed: "Fehlgeschlagen",
  rate_limited: "Rate Limit / Quota",
  skipped: "Übersprungen",
  prepared: "Vorbereitet",
};

const STATUS_TONE = {
  pending: "border-zinc-600/40 bg-zinc-700/20 text-zinc-300",
  sent: "border-emerald-500/40 bg-emerald-500/10 text-emerald-300",
  failed: "border-red-500/40 bg-red-500/10 text-red-300",
  rate_limited: "border-amber-500/40 bg-amber-500/10 text-amber-300",
  skipped: "border-white/10 bg-white/[0.04] text-zinc-400",
  prepared: "border-sky-500/40 bg-sky-500/10 text-sky-300",
};

function StatusBadge({ value, testId }) {
  if (!value) return <span data-testid={testId} className="text-zinc-500">—</span>;
  const tone = STATUS_TONE[value] || STATUS_TONE.pending;
  const label = STATUS_LABEL[value] || value;
  const Icon = value === "sent" ? MailCheck : value === "failed" || value === "rate_limited" ? MailX : value === "skipped" ? Mail : MailWarning;
  return (
    <span data-testid={testId} className={`inline-flex items-center gap-1 rounded border px-2 py-0.5 text-[11px] ${tone}`}>
      <Icon className="h-3 w-3" />
      {label}
    </span>
  );
}

export default function MailTab({ leadId, lead }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Mailto template state
  const [templateKey, setTemplateKey] = useState("");
  const [customContext, setCustomContext] = useState("");
  const [draft, setDraft] = useState(null);
  const [drafting, setDrafting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await axios.get(`${API}/admin/crm/leads/${leadId}/mail`, {
        headers: authHeaders(), timeout: 15000,
      });
      setData(r.data);
      if (!templateKey) setTemplateKey(r.data?.suggested_template_key || "personal_follow_up");
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || "Fehler");
    } finally {
      setLoading(false);
    }
  }, [leadId, templateKey]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [leadId]);

  const ms = useMemo(() => data?.mail_status || {}, [data]);
  const isManual = lead?.created_via === "admin_manual_entry";

  const errorBanner = useMemo(() => {
    const failed = [ms.auto_reply_status, ms.internal_notification_status].some(
      (s) => s === "failed" || s === "rate_limited",
    );
    if (!failed) return null;
    const lines = [];
    if (ms.auto_reply_status === "rate_limited") lines.push(`Auto-Reply Quota: ${ms.auto_reply_error || "rate_limited"}`);
    if (ms.auto_reply_status === "failed") lines.push(`Auto-Reply Fehler: ${ms.auto_reply_error || "unbekannt"}`);
    if (ms.internal_notification_status === "rate_limited") lines.push(`Interne Mail Quota: ${ms.internal_notification_error || "rate_limited"}`);
    if (ms.internal_notification_status === "failed") lines.push(`Interne Mail Fehler: ${ms.internal_notification_error || "unbekannt"}`);
    return lines;
  }, [ms]);

  const generateDraft = async () => {
    setDrafting(true);
    try {
      const r = await axios.post(
        `${API}/admin/crm/leads/${leadId}/email-draft`,
        { template_key: templateKey, custom_context: customContext || undefined },
        { headers: authHeaders(), timeout: 15000 },
      );
      setDraft(r.data);
      toast.success("Mail vorbereitet");
      await load();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = Array.isArray(detail) ? detail.map((d) => d.msg).join(" · ") : detail || err?.message;
      toast.error(msg || "Vorbereitung fehlgeschlagen");
    } finally {
      setDrafting(false);
    }
  };

  const copyTo = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} kopiert`);
    } catch {
      toast.error("Kopieren fehlgeschlagen");
    }
  };

  if (loading) {
    return (
      <section data-testid="journey-mail-tab" className="space-y-2">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="h-10 animate-pulse rounded bg-white/[0.03]" />
        ))}
      </section>
    );
  }

  return (
    <section data-testid="journey-mail-tab" className="space-y-5">
      {/* Status block */}
      <div className="rounded-md border border-white/[0.06] bg-white/[0.01] p-5">
        <header className="mb-3 flex items-center justify-between">
          <h2 className="text-sm font-semibold text-zinc-100">Automatische Mails</h2>
          {isManual ? (
            <span className="rounded border border-emerald-500/30 bg-emerald-500/10 px-2 py-0.5 text-[10px] uppercase tracking-[0.18em] text-emerald-300">
              Manuell erfasst
            </span>
          ) : null}
        </header>

        {isManual ? (
          <p data-testid="journey-mail-manual-hint" className="rounded border border-emerald-500/20 bg-emerald-500/[0.04] px-3 py-2 text-xs text-emerald-200">
            Automatische Mail wurde übersprungen, weil dieser Lead manuell erfasst wurde.
          </p>
        ) : null}

        {errorBanner ? (
          <div data-testid="journey-mail-error-banner" className="mb-3 rounded border border-red-500/30 bg-red-500/[0.05] p-3 text-xs text-red-200">
            <div className="mb-1 font-medium">Mailversand fehlgeschlagen</div>
            <ul className="space-y-0.5">
              {errorBanner.map((line, i) => <li key={i}>{line}</li>)}
            </ul>
          </div>
        ) : null}

        <div className="grid gap-3 sm:grid-cols-2">
          <MailStatusBlock
            title="Auto-Reply an Lead"
            status={ms.auto_reply_status}
            sentAt={ms.auto_reply_sent_at}
            messageId={ms.auto_reply_message_id}
            error={ms.auto_reply_error}
            testIdPrefix="journey-mail-auto"
          />
          <MailStatusBlock
            title="Interne Notification"
            status={ms.internal_notification_status}
            sentAt={ms.internal_notification_sent_at}
            messageId={ms.internal_notification_message_id}
            error={ms.internal_notification_error}
            testIdPrefix="journey-mail-internal"
          />
        </div>
      </div>

      {error ? (
        <div className="rounded-md border border-red-500/30 bg-red-500/[0.05] p-4 text-xs text-red-200">
          {String(error)}
        </div>
      ) : null}

      {/* Logs block */}
      <div className="rounded-md border border-white/[0.06] bg-white/[0.01] p-5">
        <header className="mb-3 flex items-center justify-between">
          <h2 className="text-sm font-semibold text-zinc-100">Mail-Logs</h2>
          <span className="text-[10px] text-zinc-500">letzte {(data?.logs || []).length}</span>
        </header>
        {(data?.logs || []).length === 0 ? (
          <div className="rounded border border-dashed border-white/[0.06] p-4 text-center text-[11px] text-zinc-500">
            <Inbox className="mx-auto mb-1 h-4 w-4 text-zinc-600" />
            Noch keine Mail-Einträge.
          </div>
        ) : (
          <ul className="space-y-2">
            {data.logs.map((log) => (
              <li
                key={log.id}
                data-testid={`journey-mail-log-${log.kind}-${log.status}`}
                className="rounded border border-white/[0.06] bg-white/[0.01] p-3 text-xs"
              >
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="flex flex-wrap items-center gap-2">
                    <StatusBadge value={log.status} />
                    <span className="text-zinc-300">{log.kind}</span>
                    {log.template ? <span className="text-zinc-500">· {log.template}</span> : null}
                  </div>
                  <span className="text-[10px] text-zinc-500">{formatDate(log.created_at)}</span>
                </div>
                {log.subject ? <div className="mt-1 truncate text-zinc-400">{log.subject}</div> : null}
                {log.error ? <div className="mt-1 text-red-300/80">Fehler: {log.error}</div> : null}
                {log.resend_id ? <div className="mt-0.5 text-[10px] text-zinc-600">Resend ID: {log.resend_id}</div> : null}
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Mailto draft block */}
      <div className="rounded-md border border-white/[0.06] bg-white/[0.01] p-5">
        <header className="mb-3 space-y-1">
          <h2 className="text-sm font-semibold text-zinc-100">Mail vorbereiten</h2>
          <p className="text-xs text-zinc-400">
            Erzeugt eine Vorlage und öffnet sie in Ihrem Mailprogramm. Es wird keine Mail aus dem CRM versendet.
          </p>
        </header>

        <div className="grid gap-3 sm:grid-cols-[1fr_auto]">
          <label className="space-y-1.5">
            <span className="text-[11px] uppercase tracking-[0.18em] text-zinc-400">Template</span>
            <select
              value={templateKey}
              onChange={(e) => setTemplateKey(e.target.value)}
              data-testid="journey-mail-template-select"
              className="w-full rounded border border-white/10 bg-[#0B0F10] px-3 py-2 text-sm text-zinc-100 outline-none focus:border-emerald-500/40"
            >
              {(data?.template_options || []).map((opt) => (
                <option key={opt.key} value={opt.key}>{opt.label}</option>
              ))}
            </select>
          </label>
          <button
            type="button"
            disabled={drafting || !templateKey}
            onClick={generateDraft}
            data-testid="journey-mail-generate"
            className="self-end inline-flex items-center justify-center gap-1.5 rounded bg-emerald-500 px-4 py-2 text-xs font-semibold text-zinc-950 transition hover:bg-emerald-400 disabled:opacity-50"
          >
            {drafting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
            Vorlage erzeugen
          </button>
        </div>

        <label className="mt-3 block space-y-1.5">
          <span className="text-[11px] uppercase tracking-[0.18em] text-zinc-400">Zusätzlicher Kontext (optional)</span>
          <textarea
            rows={2}
            value={customContext}
            onChange={(e) => setCustomContext(e.target.value)}
            data-testid="journey-mail-custom-context"
            placeholder="Wird vor die Signatur eingefügt — bleibt leer, wenn nicht nötig."
            className="w-full rounded border border-white/10 bg-[#0B0F10] px-3 py-2 text-sm text-zinc-100 outline-none focus:border-emerald-500/40"
          />
        </label>

        {draft ? (
          <div data-testid="journey-mail-preview" className="mt-4 space-y-3 rounded border border-emerald-500/20 bg-emerald-500/[0.03] p-4">
            <div className="grid gap-1 text-xs">
              <div><span className="text-zinc-500">An:</span> <span data-testid="journey-mail-preview-to" className="text-zinc-200">{draft.to || "—"}</span></div>
              <div><span className="text-zinc-500">Betreff:</span> <span data-testid="journey-mail-preview-subject" className="text-zinc-200">{draft.subject}</span></div>
            </div>
            <pre data-testid="journey-mail-preview-body" className="max-h-72 overflow-auto whitespace-pre-wrap rounded bg-black/30 p-3 text-[11px] leading-relaxed text-zinc-200">{draft.body}</pre>
            {draft.suggested_next_step ? (
              <div className="text-[11px] text-emerald-200/80">
                Tipp: {draft.suggested_next_step}
              </div>
            ) : null}
            <div className="flex flex-wrap gap-2">
              <a
                href={draft.mailto_url}
                data-testid="journey-mail-open-mailto"
                className="inline-flex items-center gap-1.5 rounded bg-emerald-500 px-3 py-2 text-xs font-semibold text-zinc-950 transition hover:bg-emerald-400"
              >
                <ExternalLink className="h-3.5 w-3.5" />
                Im Mailprogramm öffnen
              </a>
              <button
                type="button"
                onClick={() => copyTo(draft.subject, "Betreff")}
                data-testid="journey-mail-copy-subject"
                className="inline-flex items-center gap-1.5 rounded border border-white/10 px-3 py-2 text-xs text-zinc-300 transition hover:border-white/20 hover:bg-white/5"
              >
                <ClipboardCheck className="h-3.5 w-3.5" />
                Betreff kopieren
              </button>
              <button
                type="button"
                onClick={() => copyTo(draft.body, "Text")}
                data-testid="journey-mail-copy-body"
                className="inline-flex items-center gap-1.5 rounded border border-white/10 px-3 py-2 text-xs text-zinc-300 transition hover:border-white/20 hover:bg-white/5"
              >
                <Copy className="h-3.5 w-3.5" />
                Text kopieren
              </button>
            </div>
          </div>
        ) : null}
      </div>
    </section>
  );
}

function MailStatusBlock({ title, status, sentAt, messageId, error, testIdPrefix }) {
  return (
    <div className="space-y-1.5 rounded border border-white/[0.06] bg-white/[0.01] p-3">
      <div className="flex items-center justify-between">
        <div className="text-[11px] uppercase tracking-[0.18em] text-zinc-400">{title}</div>
        <StatusBadge value={status} testId={`${testIdPrefix}-status`} />
      </div>
      {sentAt ? <div className="text-[11px] text-zinc-400">Gesendet: <span data-testid={`${testIdPrefix}-sent-at`}>{formatDate(sentAt)}</span></div> : null}
      {messageId ? <div data-testid={`${testIdPrefix}-message-id`} className="truncate text-[10px] text-zinc-600">ID: {messageId}</div> : null}
      {error ? <div data-testid={`${testIdPrefix}-error`} className="text-[11px] text-red-300/80">{error}</div> : null}
    </div>
  );
}
